import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import {
  X,
  Pencil,
  Square,
  ArrowRight,
  Type,
  Crop,
  Undo2,
  Trash2,
  Save,
  Loader2,
} from 'lucide-react';

/**
 * ScreenshotAnnotator — full-screen modal to crop + annotate a screenshot.
 * Pure native HTML5 canvas, no external library.
 *
 * Props:
 *   - src: image src (object URL or remote URL)
 *   - onClose: callback when user dismisses without saving
 *   - onSave: callback receiving a PNG Blob of the annotated image
 */
const TOOLS = {
  pen: 'pen',
  rect: 'rect',
  arrow: 'arrow',
  text: 'text',
  crop: 'crop',
};

const COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#1f2937'];

const ScreenshotAnnotator = ({ src, onClose, onSave }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null); // HTMLImageElement
  const [tool, setTool] = useState(TOOLS.pen);
  const [color, setColor] = useState(COLORS[0]);
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [shapes, setShapes] = useState([]); // history of shapes
  const [drawing, setDrawing] = useState(false);
  const [currentShape, setCurrentShape] = useState(null);
  const [textInput, setTextInput] = useState({ visible: false, x: 0, y: 0, value: '' });
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [crop, setCrop] = useState(null); // {x, y, w, h} or null
  const [saving, setSaving] = useState(false);

  // === Load image ===
  useEffect(() => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => toast.error("Impossible de charger l'image");
    img.src = src;
  }, [src]);

  // === Redraw whenever state changes ===
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageRef.current || imgSize.w === 0) return;
    canvas.width = imgSize.w;
    canvas.height = imgSize.h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imageRef.current, 0, 0);
    // Draw all committed shapes
    shapes.forEach((s) => drawShape(ctx, s));
    // Draw the in-progress shape
    if (currentShape) drawShape(ctx, currentShape);
    // Draw crop selection overlay
    if (crop) {
      ctx.save();
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.clearRect(crop.x, crop.y, crop.w, crop.h);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(crop.x, crop.y, crop.w, crop.h);
      ctx.restore();
    }
  }, [shapes, currentShape, imgSize, crop]);

  const drawShape = (ctx, s) => {
    ctx.save();
    ctx.strokeStyle = s.color;
    ctx.fillStyle = s.color;
    ctx.lineWidth = s.strokeWidth || 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (s.type === 'pen') {
      if (s.points.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(s.points[0].x, s.points[0].y);
      for (let i = 1; i < s.points.length; i++) {
        ctx.lineTo(s.points[i].x, s.points[i].y);
      }
      ctx.stroke();
    } else if (s.type === 'rect') {
      ctx.strokeRect(s.x, s.y, s.w, s.h);
    } else if (s.type === 'arrow') {
      const { x1, y1, x2, y2 } = s;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      // arrow head
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const headLen = 14 + s.strokeWidth * 2;
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
      ctx.closePath();
      ctx.fill();
    } else if (s.type === 'text') {
      ctx.font = `bold ${s.fontSize || 20}px sans-serif`;
      ctx.textBaseline = 'top';
      // White outline for readability
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 4;
      ctx.strokeText(s.text, s.x, s.y);
      ctx.fillStyle = s.color;
      ctx.fillText(s.text, s.x, s.y);
    }
    ctx.restore();
  };

  // === Coords: convert mouse event → canvas pixel coords ===
  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvas.height;
    return { x, y };
  };

  // Position of an event inside the canvas-area parent (for absolute-positioned overlays like text input)
  const getParentRelative = (e) => {
    const canvasArea = canvasRef.current?.parentElement;
    if (!canvasArea) return { left: e.clientX, top: e.clientY };
    const rect = canvasArea.getBoundingClientRect();
    return { left: e.clientX - rect.left, top: e.clientY - rect.top };
  };

  // === Drawing handlers ===
  const handleMouseDown = (e) => {
    if (textInput.visible) return;
    const { x, y } = getCoords(e);
    if (tool === TOOLS.text) {
      // Spawn a text input at the clicked position (relative to canvas-area parent)
      const pos = getParentRelative(e);
      setTextInput({
        visible: true,
        x: pos.left,
        y: pos.top,
        canvasX: x,
        canvasY: y,
        value: '',
      });
      return;
    }
    if (tool === TOOLS.crop) {
      setCrop({ x, y, w: 0, h: 0, _start: { x, y } });
      setDrawing(true);
      return;
    }
    setDrawing(true);
    if (tool === TOOLS.pen) {
      setCurrentShape({ type: 'pen', color, strokeWidth, points: [{ x, y }] });
    } else if (tool === TOOLS.rect) {
      setCurrentShape({ type: 'rect', color, strokeWidth, x, y, w: 0, h: 0, _start: { x, y } });
    } else if (tool === TOOLS.arrow) {
      setCurrentShape({ type: 'arrow', color, strokeWidth, x1: x, y1: y, x2: x, y2: y });
    }
  };

  const handleMouseMove = (e) => {
    if (!drawing) return;
    const { x, y } = getCoords(e);
    if (tool === TOOLS.crop) {
      setCrop((prev) => {
        if (!prev) return prev;
        const start = prev._start;
        return {
          ...prev,
          x: Math.min(start.x, x),
          y: Math.min(start.y, y),
          w: Math.abs(x - start.x),
          h: Math.abs(y - start.y),
        };
      });
      return;
    }
    if (tool === TOOLS.pen && currentShape) {
      setCurrentShape({ ...currentShape, points: [...currentShape.points, { x, y }] });
    } else if (tool === TOOLS.rect && currentShape) {
      const start = currentShape._start;
      setCurrentShape({
        ...currentShape,
        x: Math.min(start.x, x),
        y: Math.min(start.y, y),
        w: Math.abs(x - start.x),
        h: Math.abs(y - start.y),
      });
    } else if (tool === TOOLS.arrow && currentShape) {
      setCurrentShape({ ...currentShape, x2: x, y2: y });
    }
  };

  const handleMouseUp = () => {
    if (!drawing) return;
    if (tool === TOOLS.crop) {
      setDrawing(false);
      return;
    }
    if (currentShape) {
      // ignore zero-size shapes
      const isTiny =
        (currentShape.type === 'rect' && currentShape.w < 4 && currentShape.h < 4) ||
        (currentShape.type === 'arrow' &&
          Math.abs(currentShape.x2 - currentShape.x1) < 4 &&
          Math.abs(currentShape.y2 - currentShape.y1) < 4);
      if (!isTiny) {
        setShapes((prev) => [...prev, currentShape]);
      }
    }
    setCurrentShape(null);
    setDrawing(false);
  };

  // === Text input handlers ===
  const commitText = () => {
    const txt = textInput.value.trim();
    if (txt) {
      setShapes((prev) => [
        ...prev,
        {
          type: 'text',
          color,
          text: txt,
          x: textInput.canvasX,
          y: textInput.canvasY,
          fontSize: 24,
        },
      ]);
    }
    setTextInput({ visible: false, x: 0, y: 0, value: '' });
  };

  const handleUndo = () => {
    setShapes((prev) => prev.slice(0, -1));
  };

  const handleClearAll = () => {
    if (!shapes.length && !crop) return;
    if (!window.confirm('Effacer toutes les annotations ?')) return;
    setShapes([]);
    setCrop(null);
  };

  const applyCrop = () => {
    if (!crop || crop.w < 5 || crop.h < 5) {
      toast.error("Sélectionnez d'abord une zone à recadrer");
      return;
    }
    // Render a CLEAN canvas (image + shapes, no crop overlay) at the source size,
    // then crop from there. Copying directly from the displayed canvas would copy
    // the cleared (transparent) pixels of the crop selection overlay.
    const cleanCanvas = document.createElement('canvas');
    cleanCanvas.width = imgSize.w;
    cleanCanvas.height = imgSize.h;
    const cleanCtx = cleanCanvas.getContext('2d');
    cleanCtx.drawImage(imageRef.current, 0, 0);
    shapes.forEach((s) => drawShape(cleanCtx, s));

    // Now crop from clean canvas
    const newCanvas = document.createElement('canvas');
    newCanvas.width = crop.w;
    newCanvas.height = crop.h;
    const newCtx = newCanvas.getContext('2d');
    newCtx.drawImage(cleanCanvas, crop.x, crop.y, crop.w, crop.h, 0, 0, crop.w, crop.h);

    const newImg = new window.Image();
    newImg.onload = () => {
      imageRef.current = newImg;
      setImgSize({ w: newImg.naturalWidth, h: newImg.naturalHeight });
      setShapes([]); // shapes are now baked into the new image
      setCrop(null);
      setTool(TOOLS.pen);
      toast.success('Image recadrée ✂️');
    };
    newImg.src = newCanvas.toDataURL('image/png');
  };

  const cancelCrop = () => setCrop(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Always render a clean canvas (image + shapes only, no crop overlay)
      const cleanCanvas = document.createElement('canvas');
      cleanCanvas.width = imgSize.w;
      cleanCanvas.height = imgSize.h;
      const cleanCtx = cleanCanvas.getContext('2d');
      cleanCtx.drawImage(imageRef.current, 0, 0);
      shapes.forEach((s) => drawShape(cleanCtx, s));
      const blob = await new Promise((resolve) =>
        cleanCanvas.toBlob(resolve, 'image/png', 0.92)
      );
      if (!blob) {
        toast.error('Erreur lors de la sauvegarde');
        return;
      }
      await onSave(blob);
    } finally {
      setSaving(false);
    }
  };

  const toolBtn = (toolName, Icon, label) => (
    <button
      key={toolName}
      onClick={() => setTool(toolName)}
      title={label}
      data-testid={`annot-tool-${toolName}`}
      className={`p-2 rounded-lg flex items-center justify-center transition ${
        tool === toolName
          ? 'bg-amber-500 text-white shadow'
          : 'bg-slate-700 text-slate-200 hover:bg-slate-600'
      }`}
    >
      <Icon className="w-5 h-5" />
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/90 z-[70] flex flex-col" data-testid="screenshot-annotator">
      {/* Toolbar — top on desktop, bottom on mobile via order */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-900 border-t md:border-t-0 md:border-b border-slate-700 flex-wrap order-2 md:order-1 overflow-x-auto">
        <div className="flex items-center gap-1.5 flex-wrap">
          {toolBtn(TOOLS.pen, Pencil, 'Crayon')}
          {toolBtn(TOOLS.rect, Square, 'Rectangle')}
          {toolBtn(TOOLS.arrow, ArrowRight, 'Flèche')}
          {toolBtn(TOOLS.text, Type, 'Texte')}
          {toolBtn(TOOLS.crop, Crop, 'Recadrer')}

          {/* Colors */}
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-700">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                title={c}
                className={`w-6 h-6 rounded-full border-2 transition ${
                  color === c ? 'border-white scale-110' : 'border-transparent'
                }`}
                style={{ backgroundColor: c }}
                data-testid={`annot-color-${c}`}
              />
            ))}
          </div>

          {/* Stroke width */}
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-700">
            {[2, 4, 7].map((w) => (
              <button
                key={w}
                onClick={() => setStrokeWidth(w)}
                title={`Épaisseur ${w}`}
                className={`w-7 h-7 rounded flex items-center justify-center ${
                  strokeWidth === w ? 'bg-amber-500' : 'bg-slate-700 hover:bg-slate-600'
                }`}
              >
                <div className="rounded-full bg-white" style={{ width: w, height: w }} />
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-700">
            <button
              onClick={handleUndo}
              disabled={!shapes.length}
              title="Annuler la dernière action"
              className="p-2 bg-slate-700 text-slate-200 hover:bg-slate-600 rounded-lg disabled:opacity-30"
              data-testid="annot-undo-btn"
            >
              <Undo2 className="w-5 h-5" />
            </button>
            <button
              onClick={handleClearAll}
              disabled={!shapes.length && !crop}
              title="Tout effacer"
              className="p-2 bg-slate-700 text-slate-200 hover:bg-red-600 rounded-lg disabled:opacity-30"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>

          {/* Crop apply / cancel */}
          {crop && crop.w > 0 && crop.h > 0 && (
            <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-700">
              <button
                onClick={applyCrop}
                className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 font-medium"
                data-testid="annot-apply-crop"
              >
                ✂️ Appliquer recadrage
              </button>
              <button
                onClick={cancelCrop}
                className="px-2 py-1.5 bg-slate-700 text-slate-200 text-sm rounded-lg hover:bg-slate-600"
              >
                Annuler
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 ml-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 text-sm"
            data-testid="annot-cancel-btn"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-bold disabled:opacity-50 flex items-center gap-2 text-sm"
            data-testid="annot-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span className="hidden sm:inline">Enregistrer</span>
          </button>
          <button onClick={onClose} className="p-2 text-slate-300 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Canvas area */}
      <div
        className="flex-1 flex items-center justify-center overflow-auto p-4 relative order-1 md:order-none"
        style={{ cursor: tool === TOOLS.text ? 'text' : tool === TOOLS.crop ? 'crosshair' : 'crosshair', touchAction: 'none' }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={handleMouseDown}
          onPointerMove={handleMouseMove}
          onPointerUp={handleMouseUp}
          onPointerCancel={handleMouseUp}
          onPointerLeave={handleMouseUp}
          className="max-w-full max-h-full object-contain shadow-2xl bg-white touch-none"
          style={{ touchAction: 'none' }}
          data-testid="annot-canvas"
        />
        {/* Text input overlay */}
        {textInput.visible && (
          <input
            autoFocus
            type="text"
            value={textInput.value}
            onChange={(e) => setTextInput((t) => ({ ...t, value: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitText();
              if (e.key === 'Escape') setTextInput({ visible: false, x: 0, y: 0, value: '' });
            }}
            onBlur={commitText}
            placeholder="Tapez votre note + Entrée"
            className="absolute z-10 px-2 py-1 border-2 border-amber-500 bg-white text-sm shadow-lg rounded"
            style={{
              left: textInput.x,
              top: textInput.y,
              color,
              fontWeight: 'bold',
            }}
            data-testid="annot-text-input"
          />
        )}
      </div>

      {/* Footer help — hidden on mobile to save space */}
      <div className="hidden md:flex px-4 py-2 bg-slate-900 text-xs text-slate-400 border-t border-slate-700 items-center gap-4 order-3">
        <span>💡 Astuce :</span>
        <span>🖊️ dessiner</span>
        <span>·</span>
        <span>🟥 encadrer une zone</span>
        <span>·</span>
        <span>➡️ pointer un élément</span>
        <span>·</span>
        <span>🅰️ ajouter du texte</span>
        <span>·</span>
        <span>✂️ recadrer pour ne garder que l'essentiel</span>
      </div>
    </div>
  );
};

export default ScreenshotAnnotator;
